import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Store, MapPin, Star, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

/* ============ CALENDAR VIEW ============ */
export default function CalendarView() {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() }; // month is 0-indexed
  });
  const [dailySummary, setDailySummary] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateDetail, setDateDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const monthStr = `${currentMonth.year}-${String(currentMonth.month + 1).padStart(2, "0")}`;
  const monthLabel = new Date(currentMonth.year, currentMonth.month).toLocaleDateString("sv-SE", { month: "long", year: "numeric" });

  useEffect(() => {
    const loadSummary = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/admin/analytics/daily-summary?month=${monthStr}`);
        setDailySummary(res.data);
      } catch (e) { /* */ }
      finally { setLoading(false); }
    };
    loadSummary();
  }, [monthStr]);

  const loadDateDetail = async (date) => {
    setSelectedDate(date);
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/analytics/date/${date}`);
      setDateDetail(res.data);
    } catch (e) { toast.error("Kunde inte ladda dagens detaljer"); }
    finally { setDetailLoading(false); }
  };

  const prevMonth = () => {
    setCurrentMonth(m => {
      if (m.month === 0) return { year: m.year - 1, month: 11 };
      return { ...m, month: m.month - 1 };
    });
    setSelectedDate(null);
    setDateDetail(null);
  };

  const nextMonth = () => {
    const now = new Date();
    const next = currentMonth.month === 11
      ? { year: currentMonth.year + 1, month: 0 }
      : { year: currentMonth.year, month: currentMonth.month + 1 };
    // Don't go beyond current month
    if (next.year > now.getFullYear() || (next.year === now.getFullYear() && next.month > now.getMonth())) return;
    setCurrentMonth(next);
    setSelectedDate(null);
    setDateDetail(null);
  };

  // Build calendar grid
  const firstDay = new Date(currentMonth.year, currentMonth.month, 1);
  const lastDay = new Date(currentMonth.year, currentMonth.month + 1, 0);
  const startDow = (firstDay.getDay() + 6) % 7; // Monday = 0
  const daysInMonth = lastDay.getDate();
  const todayStr = new Date().toISOString().slice(0, 10);

  const summaryMap = {};
  dailySummary.forEach(d => { summaryMap[d.date] = d; });

  const calendarCells = [];
  for (let i = 0; i < startDow; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${currentMonth.year}-${String(currentMonth.month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    calendarCells.push({ day: d, date: dateStr, summary: summaryMap[dateStr] || null });
  }

  const maxRevenue = Math.max(...dailySummary.map(d => d.revenue), 1);

  return (
    <div data-testid="calendar-view">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-playfair text-xl font-bold text-[#EDEDED] flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#D4B483]" /> Daglig översikt
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-2 rounded-lg bg-[#121212] border border-[#27272A] text-[#E8E8EA] hover:text-[#EDEDED] hover:border-[#3F3F46] transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-[#EDEDED] font-medium min-w-[140px] text-center capitalize">{monthLabel}</span>
          <button onClick={nextMonth} className="p-2 rounded-lg bg-[#121212] border border-[#27272A] text-[#E8E8EA] hover:text-[#EDEDED] hover:border-[#3F3F46] transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Calendar Grid */}
        <div className="lg:col-span-2 bg-[#121212] rounded-xl border border-[#27272A] p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-[#D4B483] animate-spin" /></div>
          ) : (
            <>
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"].map(d => (
                  <div key={d} className="text-center text-xs text-[#E8E8EA] font-semibold uppercase tracking-wider py-1">{d}</div>
                ))}
              </div>
              {/* Calendar cells */}
              <div className="grid grid-cols-7 gap-1">
                {calendarCells.map((cell, i) => {
                  if (!cell) return <div key={`empty-${i}`} className="aspect-square" />;
                  const isToday = cell.date === todayStr;
                  const isSelected = cell.date === selectedDate;
                  const isFuture = cell.date > todayStr;
                  const s = cell.summary;
                  const hasData = s && s.total_orders > 0;
                  // Revenue intensity for background color
                  const intensity = hasData ? Math.min((s.revenue / maxRevenue) * 0.6 + 0.1, 0.7) : 0;

                  return (
                    <button
                      key={cell.date}
                      onClick={() => !isFuture && loadDateDetail(cell.date)}
                      disabled={isFuture}
                      className={`aspect-square rounded-lg p-1 flex flex-col items-center justify-center gap-0.5 transition-all text-center relative
                        ${isSelected ? "ring-2 ring-[#D4B483] bg-[#D4B483]/15" : ""}
                        ${isToday && !isSelected ? "ring-1 ring-[#3B82F6]/50" : ""}
                        ${isFuture ? "opacity-30 cursor-not-allowed" : "hover:bg-[#1A1A1A] cursor-pointer"}
                        ${!isSelected && !isToday ? "bg-[#0A0A0A]" : ""}
                      `}
                    >
                      <span className={`text-xs font-medium ${isToday ? "text-[#3B82F6]" : isSelected ? "text-[#D4B483]" : "text-[#EDEDED]"}`}>
                        {cell.day}
                      </span>
                      {hasData && (
                        <>
                          <span className="text-[8px] text-[#E8E8EA] leading-none">{s.total_orders} ord</span>
                          <div className="w-full h-1 rounded-full bg-[#27272A] overflow-hidden mt-0.5">
                            <div className="h-full rounded-full bg-[#D4B483] transition-all" style={{ width: `${intensity * 100}%` }} />
                          </div>
                        </>
                      )}
                      {!hasData && !isFuture && (
                        <span className="text-[8px] text-[#27272A] leading-none">—</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Monthly totals */}
              {dailySummary.length > 0 && (
                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-[#27272A]">
                  <div className="text-center">
                    <p className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold">Månadens order</p>
                    <p className="text-lg font-bold text-[#EDEDED]">{dailySummary.reduce((s, d) => s + d.total_orders, 0)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold">Månadens omsättning</p>
                    <p className="text-lg font-bold text-[#D4B483]">{dailySummary.reduce((s, d) => s + d.revenue, 0).toLocaleString()} kr</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold">Snitt/dag</p>
                    <p className="text-lg font-bold text-[#EDEDED]">
                      {Math.round(dailySummary.reduce((s, d) => s + d.revenue, 0) / Math.max(dailySummary.filter(d => d.total_orders > 0).length, 1)).toLocaleString()} kr
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Date Detail Panel */}
        <div className="lg:col-span-1">
          {detailLoading ? (
            <div className="bg-[#121212] rounded-xl border border-[#27272A] p-6 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-[#D4B483] animate-spin" />
            </div>
          ) : dateDetail ? (
            <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4 sticky top-20">
              <div>
                <p className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold">Valt datum</p>
                <h3 className="text-lg font-bold text-[#EDEDED] font-playfair capitalize">
                  {new Date(dateDetail.date + "T12:00:00").toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long" })}
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#0A0A0A] rounded-lg p-3">
                  <p className="text-xs text-[#E8E8EA] font-semibold">Beställningar</p>
                  <p className="text-xl font-bold text-[#EDEDED]">{dateDetail.total_orders}</p>
                </div>
                <div className="bg-[#0A0A0A] rounded-lg p-3">
                  <p className="text-xs text-[#E8E8EA] font-semibold">Omsättning</p>
                  <p className="text-xl font-bold text-[#D4B483]">{dateDetail.total_revenue?.toLocaleString()} kr</p>
                </div>
                <div className="bg-[#0A0A0A] rounded-lg p-3">
                  <p className="text-xs text-[#E8E8EA] font-semibold">Slutförd</p>
                  <p className="text-xl font-bold text-[#10B981]">{dateDetail.completed_orders}</p>
                </div>
                <div className="bg-[#0A0A0A] rounded-lg p-3">
                  <p className="text-xs text-[#E8E8EA] font-semibold">Snittorder</p>
                  <p className="text-xl font-bold text-[#EDEDED]">{dateDetail.avg_order_value} kr</p>
                </div>
              </div>

              {dateDetail.cancelled_orders > 0 && (
                <div className="bg-[#EF4444]/5 rounded-lg p-3 border border-[#EF4444]/20">
                  <p className="text-xs text-[#EF4444] font-semibold">Avbrutna / Avvisade</p>
                  <p className="text-lg font-bold text-[#EF4444]">{dateDetail.cancelled_orders}</p>
                </div>
              )}

              {dateDetail.avg_rating && (
                <div className="bg-[#F59E0B]/5 rounded-lg p-3 border border-[#F59E0B]/20">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" />
                    <span className="text-sm font-bold text-[#EDEDED]">{dateDetail.avg_rating}</span>
                    <span className="text-xs text-[#E8E8EA]">({dateDetail.review_count} reviews)</span>
                  </div>
                </div>
              )}

              {/* Order type split */}
              <div>
                <p className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold mb-2">Ordertyp</p>
                <div className="flex gap-2">
                  <div className="flex-1 bg-[#10B981]/10 rounded-lg p-2 text-center">
                    <Store className="w-4 h-4 text-[#10B981] mx-auto mb-1" />
                    <p className="text-sm font-bold text-[#EDEDED]">{dateDetail.order_type_split?.pickup || 0}</p>
                    <p className="text-[9px] text-[#E8E8EA]">Avhämtning</p>
                  </div>
                  <div className="flex-1 bg-[#3B82F6]/10 rounded-lg p-2 text-center">
                    <MapPin className="w-4 h-4 text-[#3B82F6] mx-auto mb-1" />
                    <p className="text-sm font-bold text-[#EDEDED]">{dateDetail.order_type_split?.delivery || 0}</p>
                    <p className="text-[9px] text-[#E8E8EA]">Leverans</p>
                  </div>
                </div>
              </div>

              {/* Hourly breakdown */}
              {dateDetail.hourly_orders?.length > 0 && (
                <div>
                  <p className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold mb-2">Per timme</p>
                  <div className="space-y-1">
                    {dateDetail.hourly_orders.map(h => (
                      <div key={h.hour} className="flex items-center gap-2">
                        <span className="text-xs text-[#E8E8EA] w-8 shrink-0">{h.hour}:00</span>
                        <div className="flex-1 h-4 bg-[#0A0A0A] rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] rounded-full transition-all"
                            style={{ width: `${Math.max((h.orders / Math.max(...dateDetail.hourly_orders.map(x => x.orders))) * 100, 10)}%` }}>
                          </div>
                        </div>
                        <span className="text-xs text-[#E8E8EA] w-4 text-right">{h.orders}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Popular items */}
              {dateDetail.popular_items?.length > 0 && (
                <div>
                  <p className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold mb-2">Toppsäljare</p>
                  <div className="space-y-1.5">
                    {dateDetail.popular_items.slice(0, 5).map((item, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-xs text-[#EDEDED]">{i + 1}. {item.name}</span>
                        <Badge className="bg-[#D4B483]/15 text-[#D4B483] border-0 text-[9px]">{item.count}x</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-[#121212] rounded-xl border border-[#27272A] p-6 flex flex-col items-center justify-center text-center min-h-[200px]">
              <CalendarDays className="w-10 h-10 text-[#27272A] mb-3" />
              <p className="text-sm text-[#E8E8EA]">Välj ett datum för att se detaljer</p>
              <p className="text-xs text-[#B4B4BC] mt-1">Klicka på en dag i kalendern</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
